import type { FC, ReactElement, ReactNode } from "react"

import { AnimatePresence } from "framer-motion"
import { useShallowCompareEffect } from "react-use"

import { ETypographyColor, ETypographySize, Typography } from "@/3514/components"
import { useCoachingChatContext as useChatContext } from "@/3514/entities/coaching-chat/context"
import { idleCb } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import type { TEmptyCallback } from "@/shared/types/functions"

import { coachingChatStyles as chatStyles } from "../styles"

import { coachingChatOwlComponents as owls } from "./coaching-chat.owls-components"

const {
    conversation: { typingIndicator: classes }
} = chatStyles

function _renderDots(): ReactNode {
    return (
        <div className={classes.dotsContainer()}>
            <div className={classes.dotsWrapper()}>
                {Array.from(
                    { length: 3 },
                    (_i: number, index: number): ReactElement => (
                        <i key={`pcmcti-${index}`} className={classes.dot(index)} />
                    )
                )}
            </div>
        </div>
    )
}

type _TProps = { withText?: boolean; isDimmed?: boolean }

const Indicator: FC<_TProps> = ({ withText, isDimmed = false }: _TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        conversation: { feedRef, feedScrollerHook }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const scrollFeedToBottom: TEmptyCallback = feedScrollerHook(feedRef)

    useShallowCompareEffect((): void => {
        if (withText) idleCb(scrollFeedToBottom)
    }, [withText, scrollFeedToBottom])

    return (
        <div className={classes.holder({ isVisible: true, isDimmed })}>
            <owls.DefaultLogo fill="blue-600" />

            <div className={classes.delayerWrapper()}>
                {_renderDots()}

                <AnimatePresence initial={false} mode="popLayout">
                    {withText && (
                        <Typography
                            color={ETypographyColor.White}
                            size={ETypographySize.Small}
                            text={t("entities.coachingChat.chat.messageDelayed")}
                            className={classes.delayerText()}
                        />
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
}

Indicator.displayName = "CoachingChatTypingIndicator"

export { Indicator as CoachingChatTypingIndicator }
